import moment from "moment";

import { UpdatedAppointmentInformation } from "@/Appointments/Api/Dto/Responses/UpdatedAppointmentInformation";
import { PADisplaySettings } from "@/Appointments/Models/Settings/PADisplaySettings";
import { IPADisplaySettingsModel } from "@/Appointments/Models/UI/IPADisplaySettingsModel";
import { Appointment } from "@/Common/Api/Appointment/Dto/Appointment";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { RAMQInfo } from "@/Common/Models/RAMQ/RAMQInfo";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { ICheckable } from "@/Common/Models/UI/Inputs/ICheckable";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { UserSettings } from "@/Globals/Models/UserSettings";

class PAListAction extends BaseAction {
    private helper: MyleHelper;

    constructor() {
        super();

        this.helper = new MyleHelper();
    }

    /**
     * Reloads the appointments in the PA List based on the updated appointment information and the appointment statuses saved in the user settings.
     * @param reloadedAppointment The updated appointment information.
     * @description This method checks if the appointment is already displayed in the PA List or not. If it's already displayed, only the reloaded appointment will be updated.
     * In the case that the appointment is not displayed and its new status matches one saved in the user settings updated, it reloads the whole PA List.
     */
    public ReloadAppointmentsInPAList(reloadedAppointment: UpdatedAppointmentInformation): void {
        const currentAppointmentsDisplayed = paListStore.PAListModel.get("TableData").toArray();
        const listOfMatchesForReloadedAppointment = currentAppointmentsDisplayed.filter(p => p.AppointmentId === reloadedAppointment.AppointmentId);
        const selectedAppointmentStatusIds = this.GetSelectedAppointmentStatusIds();

        if (selectedAppointmentStatusIds.contains(reloadedAppointment.AppointmentStateLookupId) && !listOfMatchesForReloadedAppointment.length)
            return this.LoadPAList();

        if (!listOfMatchesForReloadedAppointment.length || !this.ShouldAppointmentBeUpdated(reloadedAppointment, listOfMatchesForReloadedAppointment.firstOrDefault()))
            return;

        return paListStore.ReloadSingleAppointmentInPAList(reloadedAppointment, selectedAppointmentStatusIds);
    }

    public LoadPAList(): any {
        var siteIds: string[] = [];

        if (paListStore?.PADisplaySettingsModel?.get("IsShowOnlySelectedSitesChecked")) {
            siteIds = this.helper
                .GetCheckedItemsFromICheckableList(paListStore.PADisplaySettingsModel.get("Sites"))
                .toArray()
                .filter(checkable => checkable.Checked)
                .map(x => x.Id);
        }
        else {
            siteIds = paListStore.PADisplaySettingsModel.get("Sites").toArray().map(site => site.Id);
        }

        let appointmentStatuses: string[] = null;
        let checkableAppointmentStatuses: ICheckable[] = paListStore.PADisplaySettingsModel.get("AppointmentStatuses").toArray();

        if (checkableAppointmentStatuses != null && checkableAppointmentStatuses.some(checkable => checkable.Checked == false)) {
            appointmentStatuses = checkableAppointmentStatuses
                .filter((checkable: ICheckable) => checkable.Checked == true)
                .map((checkable: ICheckable) => checkable.Id);
        }

        let canceledPatientStatus = cacheStore.FindLookupByLookupCode("CANCELLED BY PATIENT");
        let canceledStaffStatus = cacheStore.FindLookupByLookupCode("CANCELED");

        let includeCanceled = false;
        if (appointmentStatuses != null &&
            appointmentStatuses.some(appointmentStatus => appointmentStatus == canceledPatientStatus.LookupID || appointmentStatus == canceledStaffStatus.LookupID))
            includeCanceled = true;

        var requestData = {
            Start: MyleHelper.GetStandardizedDateString(moment().startOf("day").toDate()),
            End: MyleHelper.GetStandardizedDateString(moment().endOf("day").toDate()),
            SearchText: paListStore.PAListModel.get("GlobalPAListSearchText"),
            IncludeContactPoint: true,
            IncludeCancelled: includeCanceled,
            AppointmentStatusFilter: appointmentStatuses,
            SitesId: siteIds,
            RelatedEmployees: this.helper
                .GetCheckedItemsFromICheckableList(paListStore.PADisplaySettingsModel.get("EmployeesGlobalCache"))
                .toArray()
                .filter(employee => employee.Checked).map(employee => (<EmployeeUIModel>employee).Model.ContactFacet_id),
            IncludePredefinedAppointments: false,
            SkipScheduleLoad: true,
            OmitPatientlessAppointments: cacheStore.ClinicSettings.EnableNewPatientlessAppointmentBehavior,
            IncludePatientPreferredPharmacy: true
        };

        this.Execute(ServerOperations.Server.Myle_SearchAppointments, requestData);
    }

    public PrintLabelCompleteOne(appointment: Appointment, ramqInfo: RAMQInfo, type: string): void {
        if (type === "completeOne") {
            let patientRamqDoctorOrStatus = "";
            if (ramqInfo != null) {
                patientRamqDoctorOrStatus = ramqInfo.getRamqProviderName();
            }

            let isPrintingPortrait = !settingsStore.configuration.get("ArePrintLabelsPortrait");
            let isPrintingLargeLabels = settingsStore.configuration.get("IsLargePrint");

            let labelDTO = {
                PatientContactId: appointment.PatientContactId,
                PatientContactFacetId: appointment.PatientId,
                IsC1: true,
                FamilyDoctorName: patientRamqDoctorOrStatus != null ? patientRamqDoctorOrStatus : "",
                CustomHeightInches: null,
                CustomWidthInches: null,
                IsPortraitPrinting: isPrintingPortrait
            };

            if (isPrintingLargeLabels) {
                labelDTO.CustomWidthInches = 3.5;
                labelDTO.CustomHeightInches = 1.1417;
            }

            let requestData = {
                labelDTOs: [labelDTO]
            };

            this.Execute(ServerOperations.Server.Myle_Patient_GenerateLabel, requestData);
        }
        else {
            var patientAddress = "";
            if (appointment.PatientAddressesDisplay !== undefined && appointment.PatientAddressesDisplay.length > 0)
                patientAddress = appointment.PatientAddressesDisplay[0];
            else
                patientAddress = "";

            var patientPhone = "";
            if (appointment.PatientPhoneNumbers?.length > 0)
                patientPhone = appointment.PatientPhoneNumbers.map(p => PhoneNumber.stringFormattedPhone(p, true))[0];
            else
                patientPhone = "";

            var patientRamqDoctorOrStatus = "";
            var patientCVInfo = "";

            var requestData = {
                PatientFirstName: appointment.PatientFirstName,
                PatientPreferredName: appointment.PatientPreferredName,
                PatientLastName: appointment.PatientLastName,
                PatientFileNumber: appointment.PatientIdentifier1,
                PatientNAM: appointment.PatientNAM,
                PatientExpiryDate: appointment.PatientNAMExpiry,
                PatientAdress: patientAddress,
                PatientPhone: patientPhone,
                PatientDateOfBirth: appointment.PatientDateOfBirth,
                PatientRamqDoctorOrStatus: patientRamqDoctorOrStatus != null ? patientRamqDoctorOrStatus : "",
                PatientRamqCVInfo: patientCVInfo,
                LabelType: type,
                UserLanguage: sessionStore.userLanguageCode
            };

            this.Execute(ServerOperations.Server.Myle_PAList_PrintLabelCompleteOne, requestData);
        }
    }

    public GetFamilyDoctor(individualID: string): void {
        var requestData = {
            Patient: { IndividualID: individualID },
            CurrentUser: sessionStore.getCurrentEmployee().getRamqCredentials()
        };

        this.Execute(ServerOperations.Server.RAMQ_CheckSubscription, requestData);
    }

    public SaveDisplaySettings(displaySettingsModel: IPADisplaySettingsModel): void {
        const userSettings = new UserSettings();
        userSettings.PADisplay = PADisplaySettings.FromModel(displaySettingsModel);

        var requestData =
        {
            UserSettings: userSettings,
            UserId: sessionStore.userId
        };

        this.Execute(
            ServerOperations.Server.Myle_UpdateUserSettings,
            requestData,
            null,
            null,
            null,
            () => {
                cacheStore.UserSettings.PADisplay = PADisplaySettings.FromModel(displaySettingsModel);
            }
        );
    }

    /**
     * Get the selected appointment status IDs from the user display settings filter
     * @returns An array of selected appointment status IDs.
     */
    private GetSelectedAppointmentStatusIds(): string[] {
        const appointmentStatusesInSettings: ICheckable[] = paListStore.PADisplaySettingsModel.get("AppointmentStatuses").toArray();
        return appointmentStatusesInSettings
            .filter((status: ICheckable) => status.Checked)
            .map((status: ICheckable) => status.Id);
    }

    /**
     * Check if an appointment should be updated.
     * @param updatedAppointment The appointment with the updated information.
     * @param currentAppointment The appointment currently shown in the calendar.
     */
    private ShouldAppointmentBeUpdated(updatedAppointment: UpdatedAppointmentInformation, currentAppointment: Appointment): boolean {
        if (!currentAppointment)
            return false;

        return (
            updatedAppointment.StartTime !== currentAppointment.StartTime
                || updatedAppointment.EndTime !== currentAppointment.EndTime
                || updatedAppointment.AppointmentStateLookupId !== currentAppointment.AppointmentStateLookupId
        );
    }
}

export { PAListAction };
